import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { QuoteDataGatewayService } from '../../plm/shared/quote-data-gateway.service';
import { catchError, debounceTime, map, Observable, of, take } from 'rxjs';
import {
  IDayTypeFormGroup,
  IRateTierFormGroup,
} from '../../plm/rates/shared/rates.service';
import { _ViewRepeaterItemContextFactory } from '@angular/cdk/collections';
import { Delta } from 'quill/core';

export type ValueComparator<T> = (a: T, b: T) => boolean;

export function uniqueQuoteReferenceValidator(
  gateway: QuoteDataGatewayService,
  excludeId: string | null = null,
) {
  console.log('quote id to exclude: ', excludeId);
  return (
    control: AbstractControl,
  ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    if (!control.value) {
      return of(null);
    }

    const reference = control.value as string;

    return gateway.referenceExists(reference, excludeId).pipe(
      debounceTime(500),
      take(1),
      map((isTaken) => {
        return isTaken.reference_exists ? { uniqueReference: true } : null;
      }),
      catchError(() => of(null)),
    );
  };
}

export function uniqueDayType(
  arrayControl: FormArray<FormGroup<IDayTypeFormGroup>>,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.pristine) {
      return null;
    }

    if (control.dirty && !control.value) {
      return {
        valueRequired: true,
      };
    }

    const valueToTest = control.value.toLowerCase();
    const values: string[] = arrayControl.controls.map((c) =>
      c.controls.day_type_name.value.toLowerCase(),
    );

    for (const existingValue of values) {
      const areEqual = valueToTest === existingValue;

      if (areEqual) {
        return { duplicateInArray: true };
      }
    }

    return null;
  };
}

export function uniqueTierType(
  arrayControl: FormArray<FormGroup<IRateTierFormGroup>>,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.pristine) {
      return null;
    }

    if (control.dirty && !control.value) {
      return {
        valueRequired: true,
      };
    }

    const valueToTest = control.value.toLowerCase();
    const values: string[] = arrayControl.controls.map((c) =>
      c.controls.tier_type_name.value.toLowerCase(),
    );

    for (const existingValue of values) {
      const areEqual = valueToTest === existingValue;

      if (areEqual) {
        return { duplicateInArray: true };
      }
    }

    return null;
  };
}

export function deltaMinLengthValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const delta: Delta = control.value;

    if (!delta) {
      return null;
    }

    const text =
      delta?.ops
        ?.filter((op: any) => typeof op.insert === 'string')
        ?.map((op: any) => op.insert)
        ?.join('') || '';

    const trimmedLength = text.trim().length;

    return trimmedLength >= minLength
      ? null
      : {
          deltaMinLength: {
            requiredLength: minLength,
            actualLength: trimmedLength,
          },
        };
  };
}
